import { type Mission, type OrganizationManager, type TaskTypes } from "@entities";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CommentsIcon from "@mui/icons-material/Comment";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState, type FC } from "react";
import { openPhoneDialer } from "../../generic-functions/open-phone";
import { openWhatsApp } from "../../generic-functions/open-whatsapp-message";
import { VoicenterCallButton } from "../voicenter/VoicenterCallButton";
import { ImageLoaded } from "../image-loaded/ImageLoaded";
import "./MissionDetails.scss";

/**
 * Mission Details Component Props
 */
interface MissionDetailsProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Function to close the dialog */
  onClose: () => void;
  /** Mission data to display */
  mission?: Mission;
}

/**
 * Color mapping for mission priorities
 */
const priorityColors: Record<string, string> = {
  רגילה: "#2196f3",
  גבוהה: "#ff9800",
  דחופה: "#f44336",
  "": "#757575",
};

/**
 * Color mapping for mission statuses
 */
const statusColors: Record<string, string> = {
  בוצעה: "#4caf50",
  "לא בוצעה": "#f44336",
  בתהליך: "#ff9800",
  "": "#757575",
};

/**
 * MissionDetails Component
 *
 * Displays comprehensive information about a mission in a modal dialog.
 * Includes mission details, related entities, timestamps, and manager information.
 */
export const MissionDetails: FC<MissionDetailsProps> = ({
  isOpen,
  onClose,
  mission,
}) => {
  // State for manager information
  const [assignedManager, setAssignedManager] = useState<
    OrganizationManager | undefined
  >(undefined);
  const [creatorManager, setCreatorManager] = useState<
    OrganizationManager | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadManagerDetails = async () => {
      if (!mission) return;

      setIsLoading(true);
      try {
        // Load assigned manager if available
        if (mission.assignedManager) {
          setAssignedManager(assignedManager || undefined);
        } else {
          setAssignedManager(undefined);
        }

        // Load creator manager if available
        if (mission.creatorManager) {
          setCreatorManager(mission.creatorManager || undefined);
        } else {
          setCreatorManager(undefined);
        }
      } catch (error) {
        console.error("Failed to load manager details:", error);
        setAssignedManager(undefined);
        setCreatorManager(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    loadManagerDetails();
  }, [mission]);

  const formatDateString = (date: Date) => {
    return format(new Date(date), "dd/MM/yyyy HH:mm");
  };

  const isOverdue = (date: Date) => {
    return new Date(date) < new Date() && mission?.status === "PENDING";
  };

  const getRelatedEntityName = (type: TaskTypes) => {
    switch (type) {
      case "USER":
        return mission?.association
          ? `מתנדב: ${mission.association.user?.name}`
          : "ללא שיוך";
      case "GROUP":
        return mission?.group ? `קבוצה: ${mission.group.name}` : "ללא שיוך";
      case "TEAM":
        return mission?.team ? `פרוייקט: ${mission.team.name}` : "ללא שיוך";
      case "OPPORTUNITY":
        return mission?.opportunity
          ? `משימה: ${mission.opportunity.title}`
          : "ללא שיוך";
      case "AID_RELATED":
        return mission?.association
          ? `מבקש סיוע: ${mission.association.user?.name}`
          : "ללא שיוך";
      default:
        return "ללא שיוך";
    }
  };

  const getRelatedEntityIcon = () => {
    if (mission?.association) return <PersonIcon />;
    if (mission?.group) return <GroupIcon />;
    if (mission?.opportunity) return <EventIcon />;
    if (mission?.team) return <GroupIcon />;
    return null;
  };

  if (!mission) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "15px",
          padding: "0",
          height: "auto",
          maxHeight: "90vh",
          overflowY: "auto",
        },
      }}
    >
      <div className="mission-details-container">
        <div className="mission-details-header">
          <Button
            className="back-button"
            onClick={onClose}
            startIcon={<KeyboardBackspaceIcon />}
           />
          <Typography variant="h5">פרטי המטלה</Typography>
        </div>

        <div className="mission-details-content">
          <Box className="mission-status-priority">
            {mission.status && (
              <Chip
                label={mission.status}
                className="status-chip"
                style={{ backgroundColor: statusColors[mission.status] }}
              />
            )}
            <Chip
              label={mission.priority}
              icon={<PriorityHighIcon />}
              className="priority-chip"
              style={{ backgroundColor: priorityColors[mission.priority] }}
            />
            {isOverdue(mission.date) && (
              <Chip
                label="באיחור"
                className="overdue-chip"
                style={{ backgroundColor: "#d32f2f" }}
              />
            )}
          </Box>

          <Box className="mission-detail-item">
            <Typography variant="h6" className="mission-detail-title">
              <AssignmentIcon /> תיאור
            </Typography>
            <Typography variant="body1" className="mission-detail-value">
              {mission.description}
            </Typography>
          </Box>

          <Box className="mission-detail-item">
            <Typography variant="h6" className="mission-detail-title">
              <CalendarTodayIcon /> תאריך ושעה
            </Typography>
            <Typography variant="body1" className="mission-detail-value">
              {formatDateString(mission.date)}
            </Typography>
          </Box>

          {/* Related entity information - shows only if a relationship exists */}
          {(mission.types?.length || 0) > 0 && (
            <Box className="mission-detail-item">
              <Typography variant="h6" className="mission-detail-title">
                {getRelatedEntityIcon()} שיוכים
              </Typography>
              {mission.types?.map((type) => (
                <Typography
                  key={type}
                  variant="body1"
                  className="mission-detail-value"
                >
                  {getRelatedEntityName(type)}
                </Typography>
              ))}
            </Box>
          )}

          {/* Comments section - always shown, with placeholder when empty */}
          <Box className="mission-detail-item">
            <Typography variant="h6" className="mission-detail-title">
              <CommentsIcon /> הערות
            </Typography>
            <Typography variant="body1" className="mission-detail-value">
              {mission.comments || "אין הערות למשימה זו"}
            </Typography>
          </Box>

          {/* System timestamps section */}
          <Box className="mission-detail-item">
            <Typography variant="h6" className="mission-detail-title">
              <AccessTimeIcon /> זמני מערכת
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              className="mission-detail-value timestamps-container"
            >
              <Box>
                <Typography variant="caption" className="timestamp-label">
                  נוצר ב:
                </Typography>
                <Typography>{formatDateString(mission.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" className="timestamp-label">
                  עודכן ב:
                </Typography>
                <Typography>{formatDateString(mission.updatedAt)}</Typography>
              </Box>
            </Stack>
          </Box>

          {isLoading ? (
            <Box className="loading-container">
              <CircularProgress size={30} />
              <Typography>טוען פרטי מנהל...</Typography>
            </Box>
          ) : (
            <>
              {creatorManager && (
                <div className="manager-container creator-container">
                  <div className="manager-header">
                    <Typography variant="h6">יוצר המשימה</Typography>
                  </div>
                  <div className="manager-details">
                    <div className="manager-info">
                      <ImageLoaded
                        imageId={creatorManager.profileImageId}
                        alt={creatorManager.name}
                      />
                      <Typography variant="body1">
                        {creatorManager.name}
                      </Typography>
                    </div>
                  </div>
                </div>
              )}

              {assignedManager && (
                <div className="manager-container">
                  <div className="manager-header">
                    <Typography variant="h6">מנהל אחראי</Typography>
                  </div>
                  <div className="manager-details">
                    <div className="manager-info">
                      <ImageLoaded
                        imageId={assignedManager.profileImageId}
                        alt={assignedManager.name}
                      />
                      <Typography variant="body1">
                        {assignedManager.name}
                      </Typography>
                    </div>
                    <div className="manager-actions">
                      <VoicenterCallButton
                        phoneNumber={assignedManager.phoneNumber || ""}
                      />
                      <Button
                        variant="contained"
                        startIcon={<PhoneIcon />}
                        className="call-button"
                        onClick={() =>
                          openPhoneDialer(assignedManager.phoneNumber)
                        }
                      >
                        שיחה
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        className="whatsapp-button"
                        onClick={() =>
                          openWhatsApp(assignedManager.phoneNumber)
                        }
                      >
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};
